/**
 * Shipped how-to clips (public/howto/*.json, written by scripts/howto-clips.mjs at build time). A clip is the start
 * state (the bike at rest on a placement: GameWorld.respawn makes it bit-exact) plus the expert's input stream.
 * Inputs are quantized when the expert rides (demo.ts `quantize`), so the stored stream is exactly what was
 * simulated; each channel is stored as run-length-coded deltas of the integer levels.
 */
import { InputFrame, INPUT_STRIDE_V2 } from '../../sim/input';
import type { DemoClip } from './demo';
import type { Situation } from './situation';
import { situationSpot } from './situation';

/** Integer levels per channel: levers 0..255, steer and body -127..127. */
const LEVER = 255, AXIS = 127;
/** Scale of each of the 9 v2 channels (throttle, brake, front, rear, steer, body, standing, foot, autoFoot/assist). */
const SCALE = [LEVER, LEVER, LEVER, LEVER, AXIS, AXIS, 1, 1, 1] as const;

const q = (v: number, s: number) => Math.round(v * s) / s;

/** The frame the expert actually rides: every analogue value on the stored grid. */
export function quantize(i: InputFrame): InputFrame {
  return {
    ...i, throttle: q(i.throttle, LEVER), brake: q(i.brake, LEVER), frontBrake: q(i.frontBrake ?? 0, LEVER), rearBrake: q(i.rearBrake ?? 0, LEVER),
    steer: q(i.steer, AXIS), body: q(i.body, AXIS),
  };
}

export type ControlMode = 'real' | 'simplified';

export interface ClipFile {
  v: 1;
  key: string;
  situation: Situation;
  bike: string;
  mode: ControlMode;
  configHash: string;
  dt: number;
  at: DemoClip['at'];
  steps: number;
  /** Per channel: [delta, count, delta, count, ...] of integer levels (count repeats of the same delta). */
  ch: number[][];
  marks: DemoClip['marks'];
  focusStep: number;
  stars: number;
  end: DemoClip['end'];
}

/** File name of a spot's clip for a bike and control mode (the cause does not change the expert's ride). */
export const clipFileName = (s: Situation, bike: string, mode: ControlMode): string =>
  `${s.level}.${situationSpot(s).replace(/[^a-z0-9]+/gi, '-')}.${bike}.${mode}.json`;

export function encodeClip(c: DemoClip, bike: string, mode: ControlMode): ClipFile {
  const steps = c.inputs.length / INPUT_STRIDE_V2;
  const ch = SCALE.map((s, k) => {
    const out: number[] = [];
    let prev = 0;
    for (let i = 0; i < steps; i++) {
      const level = Math.round(c.inputs[i * INPUT_STRIDE_V2 + k]! * s);
      const d = level - prev;
      prev = level;
      const n = out.length;
      if (n && out[n - 2] === d) out[n - 1]!++; else out.push(d, 1);
    }
    return out;
  });
  const r = (x: number) => Math.round(x * 1000) / 1000;
  return {
    v: 1, key: c.key, situation: c.situation, bike, mode, configHash: c.configHash, dt: c.dt, at: c.at, steps, ch,
    marks: c.marks.map((m) => ({ ...m, x: r(m.x), y: r(m.y), z: r(m.z) })), focusStep: c.focusStep, stars: c.stars, end: c.end,
  };
}

export function decodeClip(f: ClipFile): DemoClip {
  if (f.v !== 1 || f.ch.length !== INPUT_STRIDE_V2) throw new Error('unknown clip format');
  const inputs = new Array<number>(f.steps * INPUT_STRIDE_V2);
  f.ch.forEach((runs, k) => {
    const s = SCALE[k]!;
    let level = 0, i = 0;
    for (let r = 0; r < runs.length; r += 2) {
      for (let n = 0; n < runs[r + 1]!; n++) { level += runs[r]!; inputs[i++ * INPUT_STRIDE_V2 + k] = level / s; }
    }
    if (i !== f.steps) throw new Error('clip channel length mismatch');
  });
  return { key: f.key, situation: f.situation, levelId: f.situation.level, at: f.at, configHash: f.configHash, dt: f.dt, inputs, marks: f.marks, focusStep: f.focusStep, stars: f.stars, end: f.end };
}
